import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactFlow from "./ReactFlow";
import { styled } from "styled-components";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Alert } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getSocket } from "../../../services/socketManager";
import { BsList } from "react-icons/bs";
import { useUser } from '../../../contexts/UserContext';
import { SketchPicker } from "react-color";

// Socket.IO 싱글톤 인스턴스 가져오기
const socket = getSocket();

const SheetContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background-color: #fff;
  position: relative;
`;

const SheetContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  background-color: #fafafa;
  /* ReactFlow Background 컴포넌트가 그리드를 표시하므로 여기서는 제거 */
  
  /* ReactFlow 배경 스타일 */
  .react-flow {
    background-color: #fafafa !important;
  }
  
  .react-flow__pane {
    background-color: #fafafa !important;
  }
  
  /* ReactFlow Background 컴포넌트가 보이도록 배경색 제거 */
  .react-flow__background {
    background-color: transparent !important;
  }
`;

const SheetTabsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  min-height: 36px;
  padding-top: 5px;
  background-color: #fafafa;
  border-bottom: 1px solid #ccc5b9;
`;

const FixedSection = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;
  flex-shrink: 0;
`;

const ScrollableTabsSection = styled.div`
  display: flex;
  align-items: flex-end;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding-right: 8px;
  scrollbar-width: thin;
  scrollbar-color: #ccc5b9 #f5f3f0;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f3f0;
    border-radius: 4px;
    margin: 0 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(to right, #ccc5b9, #d4cdc3);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.2s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to right, #b8b0a5, #c4bdb3);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb:active {
    background: linear-gradient(to right, #a8a095, #b4ada3);
  }
  
  /* 스크롤바 코너 스타일 */
  &::-webkit-scrollbar-corner {
    background: #f5f3f0;
  }
`;

// hex 색상의 상대 밝기를 계산해 어두운 색이면 true 반환
const isDarkColor = (hex) => {
    if (!hex || hex === '#ffffff') return false;
    const h = hex.replace('#', '');
    if (h.length < 6) return false;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    // 상대 밝기 (0~255), 128 미만이면 어두운 색
    return (r * 0.299 + g * 0.587 + b * 0.114) < 128;
};

const SheetTab = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px 6px 16px;
  background: ${props => {
        if (props.$isDragging) return 'rgba(204, 197, 185, 0.5)';
        if (props.$color && props.$color !== '#ffffff') return props.$color;
        return '#CCC5B94F';
    }};
  border: ${props => props.$active
        ? '1px solid #ccc5b9'
        : '1px solid rgba(204, 197, 185, 0.3)'};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: none;
  margin-right: 4px;
  margin-top: 4px;
  cursor: pointer;
  user-select: none;
  min-width: 100px;
  max-width: 220px;
  flex-shrink: 0;
  position: relative;
  height: 32px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active
        ? '0 -2px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05)'
        : '0 1px 3px rgba(0, 0, 0, 0.05)'};
  
  /* 드래그 오버 시 왼쪽 인디케이터 */
  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 5px;
    bottom: -1px;
    width: 3px;
    background: ${props => props.$isDragOver ? '#b8b0a5' : 'transparent'};
    border-radius: 2px;
  }
  
  &:hover {
    background: 'rgba(204, 197, 185, 0.2)';
    border-color: ${props => props.$active
        ? '#ccc5b9'
        : 'rgba(204, 197, 185, 0.5)'};
    transform: 'translateY(-1px)';
    box-shadow: ${props => props.$active
        ? '0 -2px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05)'
        : '0 2px 6px rgba(204, 197, 185, 0.2)'};
  }
  
  &:hover .tab-menu-button {
    opacity: 1;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TabLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => props.$dark ? (props.$active ? '#ffffff' : 'rgba(255,255,255,0.85)') : (props.$active ? '#212529' : '#495057')};
  padding-right: ${props => props.$editing ? '0' : '24px'};
  transition: color 0.2s ease;
`;

const TabInput = styled.input`
  position: absolute;
  left: 8px;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid #aaa;
  outline: none;
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 500;
  background-color: #fff;
  color: #333;
  border-radius: 3px;
  box-sizing: border-box;
  
  &:focus {
    border-color: #888;
  }
`;

const TabMenuButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  color: ${props => props.$dark ? 'rgba(255,255,255,0.85)' : '#6c757d'};
  border-radius: 4px;
  width: 20px;
  height: 20px;
  
  &:hover {
    background-color: ${props => props.$dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)'};
    color: ${props => props.$dark ? '#ffffff' : '#333'};
    opacity: 1 !important;
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  svg {
    font-size: 16px;
  }
`;

const AddTabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: #CCC5B94F;
  border: 1px solid #ced4da;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  cursor: pointer;
  margin-right: 4px;
  margin-top: 4px;
  height: 32px;
  min-width: 36px;
  color: #495057;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(to bottom, #f1f3f5 0%, #e9ecef 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    color: #212529;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 18px;
    transition: transform 0.2s ease;
  }
  
//   &:hover svg {
//     transform: rotate(90deg);
//   }
`;

function MatrixBoard() {
    const { fileId: urlParam } = useParams(); // URL 파라미터 (board_cd 또는 숫자 file_id)
    // board_cd면 file-info에서 resolve, 숫자면 바로 사용
    const [fileId, setFileId] = React.useState(/^\d+$/.test(urlParam) ? urlParam : null);
    const navigate = useNavigate();
    const location = useLocation();
    // 개인 프로젝트 여부 (API에서 가져온 projectType 사용)
    const [isPersonal, setIsPersonal] = useState(false);
    const [isPersonalLoaded, setIsPersonalLoaded] = useState(false); // API 응답 전까지 인원수 표시 숨김
    const [tabs, setTabs] = useState([]);
    const { user, permissions, hasPermission, loading: userLoading } = useUser();
    // localStorage에서 저장된 activeTabId 복원 (urlParam 기반 키)
    const getStoredActiveTabId = () => {
        if (!urlParam) return 1;
        try {
            const stored = localStorage.getItem(`matrixBoard_activeTabId_${urlParam}`);
            return stored ? parseInt(stored, 10) : 1;
        } catch (error) {
            console.error('localStorage에서 activeTabId 읽기 실패:', error);
            return 1;
        }
    };
    const [activeTabId, setActiveTabId] = useState(getStoredActiveTabId);
    const [editingTabId, setEditingTabId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const socketIdRef = useRef(null); // 자신의 소켓 ID 저장
    const pendingUpdatesRef = useRef(new Set()); // 처리 중인 업데이트 추적
    const activeTabIdRef = useRef(activeTabId); // activeTabId를 ref로 추적
    const tabsUpdateTimeoutRef = useRef(null); // 탭 업데이트 지연 타이머
    const updatingTabNamesRef = useRef(new Set()); // 이름 업데이트 중인 탭 ID 추적
    const tabsInitRequestedRef = useRef(new Set()); // 탭 초기화 요청 중인 fileId 추적 (중복 요청 방지)
    const pendingTabAddRef = useRef(null); // 추가 중인 시트 ID (서버 응답 시 activeTabId 보호용)
    const [cadData, setCadData] = useState([]);
    const [pageTitle, setPageTitle] = useState('Matrix Board'); // 페이지 제목 상태
    const [boardMapType, setBoardMapType] = useState(null); // 보드 맵타입 (C/S/G)
    const [boardBrdCd, setBoardBrdCd] = useState(null); // 보드 브랜드 코드
    const [boardSesn, setBoardSesn] = useState(null); // 보드 시즌
    const [boardAutoUpdate, setBoardAutoUpdate] = useState(false); // 판매맵 자동 업데이트 여부
    const [boardEndDt, setBoardEndDt] = useState(null); // 판매맵 기준일자
    const [tabsLoaded, setTabsLoaded] = useState(false); // 탭 목록 로드 완료 여부
    const [templateGenerated, setTemplateGenerated] = useState(false); // 템플릿 생성 여부
    const [tabMenuAnchor, setTabMenuAnchor] = useState(null); // 탭 메뉴 앵커
    const [selectedTabForMenu, setSelectedTabForMenu] = useState(null); // 메뉴를 열 탭
    const [draggedTabId, setDraggedTabId] = useState(null); // 드래그 중인 탭 ID
    const [dragOverTabId, setDragOverTabId] = useState(null); // 드래그 오버 중인 탭 ID
    const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false); // 세션 만료 모달
    const [showReconnectingToast, setShowReconnectingToast] = useState(false); // 연결 중 토스트
    const [showColorDialog, setShowColorDialog] = useState(false); // 시트 색상 변경 다이얼로그
    const [selectedTabColor, setSelectedTabColor] = useState('#ffffff'); // 선택된 시트 색상
    const [tabForColorChange, setTabForColorChange] = useState(null); // 색상 변경할 탭 정보

    // 시트 히스토리 관리 (undo/redo용)
    const tabsHistoryRef = useRef([]); // 시트 히스토리 스택
    const tabsHistoryIndexRef = useRef(-1); // 현재 시트 히스토리 인덱스
    const MAX_TABS_HISTORY_SIZE = 50; // 최대 시트 히스토리 개수
    const isTabsUndoRedoRef = useRef(false); // 시트 undo/redo 작업 중인지 추적

    // activeTabId 변경 시 ref 업데이트 및 localStorage에 저장
    useEffect(() => {
        activeTabIdRef.current = activeTabId;
        // localStorage에 저장 (urlParam 기반 키)
        if (urlParam) {
            try {
                localStorage.setItem(`matrixBoard_activeTabId_${urlParam}`, activeTabId.toString());
            } catch (error) {
                console.error('localStorage에 activeTabId 저장 실패:', error);
            }
        }
    }, [activeTabId, fileId]);

    // urlParam(board_cd 또는 숫자)으로 파일 정보 조회 + fileId resolve
    useEffect(() => {
        if (urlParam) {
            fetch(`/api/matrix-board/file-info?fileId=${urlParam}`)
                .then(response => response.json())
                .then(result => {
                    if (result.success && result.data) {
                        setPageTitle(result.data.title || 'Matrix Board');
                        setBoardMapType(result.data.mapType || null);
                        setBoardBrdCd(result.data.brdCd || null);
                        setBoardSesn(result.data.sesn || null);
                        setBoardAutoUpdate(result.data.autoUpdate || false);
                        setBoardEndDt(result.data.endDt || null);
                        setIsPersonal(result.data.projectType === 'personal');
                        // board_cd URL인 경우 integer fileId로 resolve
                        const resolvedId = String(result.data.id);
                        if (!fileId) {
                            setFileId(resolvedId);
                        }
                        // 판매맵 + 자동업데이트: 기준일 변경 시 카드 갱신
                        if (result.data.mapType === 'S' && result.data.autoUpdate) {
                            // KST 기준 지난 일요일 계산 (오늘이 일요일이어도 전주 일요일)
                            const nowKST = new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
                            const dayOfWeek = nowKST.getUTCDay(); // 0=일, 1=월, ..., 6=토
                            const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
                            const lastSundayKST = new Date(nowKST);
                            lastSundayKST.setUTCDate(nowKST.getUTCDate() - diff);
                            const y = lastSundayKST.getUTCFullYear();
                            const m = String(lastSundayKST.getUTCMonth() + 1).padStart(2, '0');
                            const d = String(lastSundayKST.getUTCDate()).padStart(2, '0');
                            const currentEndDt = `${y}-${m}-${d}`;
                            if (result.data.endDt !== currentEndDt) {
                                fetch('/api/matrix-board/update-sales-cards', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ fileId: resolvedId })
                                })
                                    .then(r => r.json())
                                    .then(r => {
                                        if (r.success) {
                                            setBoardEndDt(r.endDate);
                                            console.log(`✅ 판매맵 자동 업데이트 완료: ${r.updatedCount}개, ${r.endDate}`);
                                        }
                                    })
                                    .catch(err => console.error('판매맵 자동 업데이트 오류:', err));
                            }
                        }
                    } else {
                        setPageTitle('Matrix Board');
                    }
                    setIsPersonalLoaded(true);
                })
                .catch(error => {
                    console.error('파일 정보 로드 오류:', error);
                    setPageTitle('Matrix Board');
                    setIsPersonalLoaded(true);
                });
        } else {
            setPageTitle('Matrix Board');
        }
    }, [urlParam]); // eslint-disable-line react-hooks/exhaustive-deps

    // 소켓 연결 및 탭 동기화
    const socketInitializedRef = useRef(false);
    useEffect(() => {
        // React Strict Mode에서 중복 실행 방지
        if (socketInitializedRef.current) {
            return;
        }
        socketInitializedRef.current = true;

        console.log('소켓 연결 및 탭 동기화');

        // 서버 헬스 체크 (선택적)
        const checkServerHealth = async () => {
            try {
                const healthUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                    ? `http://localhost:3000/health`
                    // 현재 origin 기준 (ALB Ingress 가 /health 를 api 로 라우팅). 환경별 도메인을
                    // 하드코딩하면 dev 가 prd 를 호출한다 (실제로 그랬음).
                    : `${window.location.origin}/health`;
                const response = await fetch(healthUrl);
                if (response.ok) {
                    const data = await response.json();
                    console.log('✅ 서버 헬스 체크 성공:', data);
                }
            } catch (error) {
                console.warn('⚠️ 서버 헬스 체크 실패 (서버가 실행되지 않았을 수 있음):', error.message);
            }
        };

        // 헬스 체크 실행 (비동기, 연결을 막지 않음)
        checkServerHealth();

        // 연결 상태 로깅
        const handleSocketConnect = () => {
            console.log('✅ Socket.IO 연결 성공:', socket.id);
        };

        const handleSocketDisconnect = (reason) => {
            console.warn('⚠️ Socket.IO 연결 해제:', reason);
        };

        const handleConnectError = (error) => {
            const socketUrl = socket.io.uri || window.location.origin;
            console.error('❌ Socket.IO 연결 오류:', error);
            console.error('연결 시도 URL:', `${socketUrl}/socket.io/`);
            console.error('서버가 실행 중인지 확인하세요.');
            if (error.message === 'timeout') {
                console.error('타임아웃: 서버가 응답하지 않습니다. 서버가 실행 중인지 확인하세요.');
            }
            if (error.message && error.message.includes('CORS')) {
                console.error('CORS 오류: 서버의 ALLOWED_ORIGINS 환경 변수에 현재 도메인이 포함되어 있는지 확인하세요.');
            }
        };

        socket.on('connect', handleSocketConnect);
        socket.on('disconnect', handleSocketDisconnect);
        socket.on('connect_error', handleConnectError);

        // 서버에서 탭 목록 초기화 요청
        const handleTabsInit = (serverTabs) => {
            // 요청 완료 표시 (모든 요청 중인 fileId 제거 - 서버가 어떤 fileId에 대한 응답인지 알 수 없으므로)
            // 일반적으로 가장 최근 요청에 대한 응답이므로 모든 요청을 완료 처리
            tabsInitRequestedRef.current.clear();
            if (serverTabs && Array.isArray(serverTabs) && serverTabs.length > 0) {
                // 시트 히스토리 초기화 (서버에서 로드한 시트 목록을 초기 상태로 저장)
                tabsHistoryRef.current = [{
                    tabs: JSON.parse(JSON.stringify(serverTabs)),
                    activeTabId: activeTabIdRef.current,
                    action: 'init'
                }];
                tabsHistoryIndexRef.current = 0;

                setTabs(serverTabs);
                // 탭 목록 로드 후 저장된 activeTabId가 유효한지 확인
                if (!tabsLoaded) {
                    const storedTabId = getStoredActiveTabId();
                    const storedTabExists = serverTabs.find(t => t.id === storedTabId);
                    if (storedTabExists) {
                        // 저장된 탭이 존재하면 복원
                        setActiveTabId(storedTabId);
                    } else if (!serverTabs.find(t => t.id === activeTabIdRef.current)) {
                        // 저장된 탭이 없고 현재 탭도 없으면 첫 번째 탭 선택
                        setActiveTabId(serverTabs[0].id);
                    }
                } else if (!serverTabs.find(t => t.id === activeTabIdRef.current)) {
                    // 탭이 이미 로드된 경우에만 첫 번째 탭으로 변경
                    setActiveTabId(serverTabs[0].id);
                }
            } else {
                // 서버에 탭이 없으면 기본 탭 생성하지 않음 (템플릿 자동생성 대기)
                setTabs([]);
                console.log('서버에 탭이 없습니다. 템플릿 자동생성 대기 중...');
            }
            setTabsLoaded(true); // 탭 목록 로드 완료
        };

        // 다른 클라이언트의 탭 변경사항 받기
        const handleTabsUpdate = (data) => {
            // 전체 동기화 이벤트 처리
            if (data.type === 'sync' && data.tabs && data.fileId === fileId) {
                console.log('✅ 전체 탭 목록 동기화:', data.tabs);

                // 기존 탭 업데이트 타이머가 있으면 취소
                if (tabsUpdateTimeoutRef.current) {
                    clearTimeout(tabsUpdateTimeoutRef.current);
                }

                // 이름이 변경되는 탭 ID 추적
                setTabs(prevTabs => {
                    const tabsMap = new Map(prevTabs.map(t => [t.id, t]));
                    const nameChangedTabs = new Set();

                    data.tabs.forEach(newTab => {
                        const existingTab = tabsMap.get(newTab.id);
                        if (existingTab && existingTab.name !== newTab.name) {
                            nameChangedTabs.add(newTab.id);
                        }
                    });

                    // 이름이 변경되는 탭들을 업데이트 중으로 표시
                    nameChangedTabs.forEach(tabId => {
                        updatingTabNamesRef.current.add(tabId);
                    });

                    // 이름이 변경되는 탭의 이름을 일시적으로 숨김
                    return prevTabs.map(tab => {
                        if (nameChangedTabs.has(tab.id)) {
                            return { ...tab, name: '' }; // 이름 숨김
                        }
                        return tab;
                    });
                });

                // 약간의 지연을 두어 업데이트가 완료된 후에만 이름 변경 (깜빡임 방지)
                tabsUpdateTimeoutRef.current = setTimeout(() => {
                    setTabs(prevTabs => {
                        const tabsMap = new Map(prevTabs.map(t => [t.id, t]));
                        const updatedTabs = data.tabs.map(newTab => {
                            const existingTab = tabsMap.get(newTab.id);
                            // 기존 탭이 있으면 기존 정보를 유지하고 이름과 색상 업데이트
                            if (existingTab) {
                                // 업데이트 완료 후 이름 표시
                                updatingTabNamesRef.current.delete(newTab.id);
                                return {
                                    ...existingTab,
                                    name: newTab.name,
                                    color: newTab.color || existingTab.color // 색상도 업데이트
                                };
                            }
                            // 기존 탭이 없으면 새 탭 정보 사용
                            return newTab;
                        });
                        return updatedTabs;
                    });
                    tabsUpdateTimeoutRef.current = null;
                }, 100); // 100ms 지연으로 깜빡임 방지

                // 활성 탭이 삭제된 경우에만 첫 번째 탭으로 변경 (즉시 처리)
                // 단, 시트 추가 중이면 새로 추가한 시트를 활성화 상태로 유지
                const currentActiveTabExists = data.tabs.find(t => t.id === activeTabIdRef.current);
                if (data.tabs.length > 0 && !currentActiveTabExists) {
                    // 시트 추가 중이 아니고, 현재 활성 탭이 삭제된 경우에만 첫 번째 탭으로 변경
                    if (!pendingTabAddRef.current || !data.tabs.find(t => t.id === pendingTabAddRef.current)) {
                        setActiveTabId(data.tabs[0].id);
                    } else {
                        // 시트 추가 중이면 새로 추가한 시트를 활성화
                        setActiveTabId(pendingTabAddRef.current);
                        activeTabIdRef.current = pendingTabAddRef.current;
                        pendingTabAddRef.current = null;
                    }
                } else if (pendingTabAddRef.current && data.tabs.find(t => t.id === pendingTabAddRef.current)) {
                    // 시트 추가가 완료되었고 새 시트가 목록에 있으면 활성화 상태 유지
                    if (activeTabIdRef.current !== pendingTabAddRef.current) {
                        setActiveTabId(pendingTabAddRef.current);
                        activeTabIdRef.current = pendingTabAddRef.current;
                    }
                    pendingTabAddRef.current = null;
                }
                return;
            }

            // 자신이 보낸 이벤트인지 확인 (sync 타입이 아닌 경우만)
            if (data.socketId === socketIdRef.current && data.type !== 'sync') {
                console.log('자신이 보낸 탭 업데이트 무시:', data);
                return;
            }

            // 중복 처리 방지
            const updateKey = `${data.type}-${data.tabId || (data.tab?.id)}-${data.name || ''}`;
            if (pendingUpdatesRef.current.has(updateKey)) {
                console.log('중복 업데이트 무시:', updateKey);
                return;
            }
            pendingUpdatesRef.current.add(updateKey);
            setTimeout(() => {
                pendingUpdatesRef.current.delete(updateKey);
            }, 1000);

            console.log('다른 클라이언트 탭 변경사항 받음:', data);
            if (data.type === 'add') {
                setTabs(prev => {
                    if (!prev.find(t => t.id === data.tab.id)) {
                        console.log('✅ 탭 추가 적용:', data.tab);
                        return [...prev, data.tab];
                    }
                    return prev;
                });
            } else if (data.type === 'delete') {
                setTabs(prev => {
                    const newTabs = prev.filter(t => t.id !== data.tabId);
                    if (newTabs.length > 0 && activeTabIdRef.current === data.tabId) {
                        setActiveTabId(newTabs[0].id);
                    }
                    console.log('✅ 탭 삭제 적용:', data.tabId);
                    return newTabs;
                });
            } else if (data.type === 'rename') {
                setTabs(prev => {
                    const updated = prev.map(tab =>
                        tab.id === data.tabId ? { ...tab, name: data.name } : tab
                    );
                    console.log('✅ 탭 이름 변경 적용:', data.tabId, data.name);
                    return updated;
                });
            } else if (data.type === 'color') {
                setTabs(prev => {
                    const updated = prev.map(tab =>
                        tab.id === data.tabId ? { ...tab, color: data.color } : tab
                    );
                    console.log('✅ 탭 색상 변경 적용:', data.tabId, data.color);
                    return updated;
                });
            }
        };

        // 소켓 연결 처리
        const handleConnect = () => {
            socketIdRef.current = socket.id;
            console.log('소켓 연결됨, 탭 목록 요청:', socket.id);
            // 연결 시 fileId가 있으면 탭 목록 요청
            if (fileId) {
                socket.emit("tabs-init", fileId);
            }
        };

        // 소켓 연결 상태 확인 및 명시적 연결 시도
        if (socket.connected) {
            socketIdRef.current = socket.id;
            // 이미 연결되어 있으면 fileId가 있을 때만 요청
            if (fileId) {
                socket.emit("tabs-init", fileId);
            }
        } else {
            // 연결되지 않았으면 명시적으로 연결 시도
            console.log('소켓이 연결되지 않음, 연결 시도 중...');
            socket.connect();
        }

        // 이벤트 리스너 등록
        socket.on("connect", handleConnect);
        // 시트 이름 변경 에러 처리
        const handleTabsUpdateError = (data) => {
            if (data.type === 'rename' && data.error) {
                alert(data.error || '시트 이름 변경 중 오류가 발생했습니다.');
                // 에러 발생 시 탭 목록 다시 로드하여 원래 이름으로 복원
                if (fileId) {
                    socket.emit("tabs-init", fileId);
                }
            }
        };

        // 보드 편집(auto_update 등) 반영
        const handleFilesUpdate = (data) => {
            if (data?.type === 'update' && data?.file?.id && String(data.file.id) === String(fileId)) {
                if (data.file.autoUpdate !== undefined) {
                    setBoardAutoUpdate(data.file.autoUpdate);
                }
            }
        };

        socket.on("tabs-init", handleTabsInit);
        socket.on("tabs-update", handleTabsUpdate);
        socket.on("tabs-update-error", handleTabsUpdateError);
        socket.on("files-update", handleFilesUpdate);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("connect", handleSocketConnect);
            socket.off("disconnect", handleSocketDisconnect);
            socket.off("connect_error", handleConnectError);
            socket.off("tabs-init", handleTabsInit);
            socket.off("tabs-update", handleTabsUpdate);
            socket.off("tabs-update-error", handleTabsUpdateError);
            socket.off("files-update", handleFilesUpdate);
            // 탭 업데이트 타이머 정리
            if (tabsUpdateTimeoutRef.current) {
                clearTimeout(tabsUpdateTimeoutRef.current);
                tabsUpdateTimeoutRef.current = null;
            }
            socketInitializedRef.current = false;
        };
    }, []); // 의존성 배열을 비워서 한 번만 실행되도록 함

    // fileId가 변경될 때 탭 목록 다시 요청
    useEffect(() => {
        if (!fileId) return;

        // 이미 요청 중인 fileId면 중복 요청 방지
        if (tabsInitRequestedRef.current.has(fileId)) {
            return;
        }

        const requestTabs = () => {
            // 요청 시작 표시
            tabsInitRequestedRef.current.add(fileId);
            console.log('파일 변경됨, 탭 목록 다시 요청:', fileId);
            socket.emit("tabs-init", fileId);
        };

        if (socket.connected) {
            // 이미 연결되어 있으면 즉시 요청
            requestTabs();
        } else {
            // 연결되지 않았으면 연결을 기다린 후 요청
            const handleConnect = () => {
                console.log('소켓 연결됨, 파일 탭 목록 요청:', fileId);
                requestTabs();
                socket.off("connect", handleConnect);
            };
            socket.on("connect", handleConnect);

            // 연결 시도
            if (!socket.connecting) {
                socket.connect();
            }

            return () => {
                socket.off("connect", handleConnect);
            };
        }
    }, [fileId]);

    // 소켓 재연결 및 세션 만료 이벤트 처리
    useEffect(() => {
        const handleReconnecting = () => {
            console.log('🔄 소켓 재연결 시도 중...');
            setShowReconnectingToast(true);
        };

        const handleReconnected = () => {
            console.log('✅ 소켓 재연결 성공');
            setShowReconnectingToast(false);
            setShowSessionExpiredModal(false);
        };

        const handleSessionExpired = () => {
            console.error('❌ 세션 만료 - 재연결 실패');
            setShowReconnectingToast(false);
            setShowSessionExpiredModal(true);
        };

        const handleReconnectAttempt = () => {
            console.log('🔄 재연결 시도 중...');
            setShowReconnectingToast(true);
        };

        // 커스텀 이벤트 리스너 등록
        window.addEventListener('socket:reconnecting', handleReconnecting);
        window.addEventListener('socket:reconnected', handleReconnected);
        window.addEventListener('socket:session-expired', handleSessionExpired);
        window.addEventListener('socket:reconnect-attempt', handleReconnectAttempt);

        return () => {
            window.removeEventListener('socket:reconnecting', handleReconnecting);
            window.removeEventListener('socket:reconnected', handleReconnected);
            window.removeEventListener('socket:session-expired', handleSessionExpired);
            window.removeEventListener('socket:reconnect-attempt', handleReconnectAttempt);
        };
    }, []);

    // 세션 만료 모달 닫기 및 새로고침
    const handleRefresh = () => {
        window.location.reload();
    };

    const handleCloseSessionModal = () => {
        setShowSessionExpiredModal(false);
    };

    // 시트 히스토리에 저장
    const saveTabsToHistory = useCallback((newTabs, action = 'unknown', newActiveTabId = null) => {
        if (isTabsUndoRedoRef.current) {
            return; // undo/redo 작업 중이면 히스토리 저장 건너뜀
        }

        // Undo 후 새 작업 시: 현재 인덱스 이후의 히스토리 제거
        if (tabsHistoryIndexRef.current < tabsHistoryRef.current.length - 1) {
            tabsHistoryRef.current = tabsHistoryRef.current.slice(0, tabsHistoryIndexRef.current + 1);
        }

        // activeTabId는 새로 설정할 값이 있으면 사용, 없으면 현재 값 사용
        const activeTabIdToSave = newActiveTabId !== null ? newActiveTabId : activeTabIdRef.current;

        // 현재 시트 상태를 히스토리에 저장
        tabsHistoryRef.current.push({
            tabs: JSON.parse(JSON.stringify(newTabs)), // 깊은 복사
            activeTabId: activeTabIdToSave,
            action: action
        });

        // 최대 히스토리 크기 제한
        if (tabsHistoryRef.current.length > MAX_TABS_HISTORY_SIZE) {
            tabsHistoryRef.current.shift();
        } else {
            tabsHistoryIndexRef.current++;
        }

        console.log('📝 시트 히스토리 저장:', {
            action,
            tabsCount: newTabs.length,
            activeTabId: activeTabIdToSave,
            historyIndex: tabsHistoryIndexRef.current,
            historyLength: tabsHistoryRef.current.length
        });
    }, []);

    // 시트 히스토리 복원 (undo/redo용)
    const restoreTabsFromHistory = useCallback((direction) => {
        if (direction === 'undo') {
            if (tabsHistoryIndexRef.current > 0) {
                isTabsUndoRedoRef.current = true;
                tabsHistoryIndexRef.current--;
                const historyState = tabsHistoryRef.current[tabsHistoryIndexRef.current];
                if (historyState) {
                    setTabs(historyState.tabs);
                    setActiveTabId(historyState.activeTabId);
                    console.log('↩️ 시트 Undo:', {
                        historyIndex: tabsHistoryIndexRef.current,
                        tabsCount: historyState.tabs.length,
                        activeTabId: historyState.activeTabId
                    });
                }
                setTimeout(() => {
                    isTabsUndoRedoRef.current = false;
                }, 100);
            }
        } else if (direction === 'redo') {
            if (tabsHistoryIndexRef.current < tabsHistoryRef.current.length - 1) {
                isTabsUndoRedoRef.current = true;
                tabsHistoryIndexRef.current++;
                const historyState = tabsHistoryRef.current[tabsHistoryIndexRef.current];
                if (historyState) {
                    setTabs(historyState.tabs);
                    setActiveTabId(historyState.activeTabId);
                    console.log('↪️ 시트 Redo:', {
                        historyIndex: tabsHistoryIndexRef.current,
                        tabsCount: historyState.tabs.length,
                        activeTabId: historyState.activeTabId
                    });
                }
                setTimeout(() => {
                    isTabsUndoRedoRef.current = false;
                }, 100);
            }
        }
    }, []);

    const handleAddTab = async () => {
        try {
            // 서버에서 다음 시트 번호 조회 (삭제된 시트 포함하여 정확한 번호 계산)
            const response = await fetch(`/api/matrix-board/next-sheet-num?fileId=${fileId}`);
            const result = await response.json();

            let newId;
            if (result.success && result.nextSheetNum) {
                newId = result.nextSheetNum;
            } else {
                // API 실패 시 기존 로직 사용 (fallback)
                newId = Math.max(...tabs.map(t => t.id), 0) + 1;
            }

            const newTab = {
                id: newId,
                name: `시트${newId}`
            };
            const newTabs = [...tabs, newTab];

            console.log('➕ 시트 추가 및 선택:', {
                newTabId: newId,
                newTabName: newTab.name,
                previousActiveTabId: activeTabIdRef.current,
                totalTabs: newTabs.length,
                fileId: fileId,
                fromServer: result.success
            });

            // 새 시트를 활성화 (ref도 즉시 업데이트하여 서버 응답 시 덮어쓰지 않도록)
            activeTabIdRef.current = newId;
            setActiveTabId(newId);

            // 시트 추가 중임을 표시 (서버 응답 시 activeTabId 보호용)
            pendingTabAddRef.current = newId;

            // 히스토리에 저장 (새 시트 ID를 activeTabId로 저장)
            saveTabsToHistory(newTabs, 'add', newId);

            setTabs(newTabs);

            // 서버에 탭 추가 알림 (소켓 ID 포함)
            socket.emit("tabs-update", {
                type: 'add',
                tab: newTab,
                fileId: fileId || 'default',
                socketId: socket.id,
                createdBy: user?.name || null
            });
            console.log('탭 추가 전송:', newTab);
        } catch (error) {
            console.error('시트 추가 오류:', error);
            alert('시트 추가 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteTab = (e, tabId) => {
        e.stopPropagation();
        if (tabs.length === 1) {
            alert("최소 하나의 시트는 필요합니다.");
            return;
        }

        const tabToDelete = tabs.find(tab => tab.id === tabId);
        const tabName = tabToDelete?.name || `시트${tabId}`;

        // 확인 알림창
        if (!window.confirm(`${tabName}를 영구적으로 삭제하시겠습니까?`)) {
            return; // 취소 시 삭제하지 않음
        }

        const newTabs = tabs.filter(tab => tab.id !== tabId);
        const newActiveTabId = activeTabId === tabId ? newTabs[0].id : activeTabId;

        // 히스토리에 저장
        saveTabsToHistory(newTabs, 'delete');

        setTabs(newTabs);
        if (activeTabId === tabId) {
            setActiveTabId(newActiveTabId);
        }

        // 서버에 탭 삭제 알림 (소켓 ID 포함)
        socket.emit("tabs-update", {
            type: 'delete',
            tabId: tabId,
            fileId: fileId || 'default',
            socketId: socket.id
        });
        console.log('탭 삭제 전송:', tabId);
    };

    // 탭 메뉴 열기
    const handleTabMenuOpen = (e, tab) => {
        e.stopPropagation();
        setTabMenuAnchor(e.currentTarget);
        setSelectedTabForMenu(tab);
    };

    // 탭 메뉴 닫기
    const handleTabMenuClose = () => {
        setTabMenuAnchor(null);
        setSelectedTabForMenu(null);
    };

    // 시트명 수정 활성화 (기존 더블클릭 기능 활용)
    const handleOpenRenameTab = () => {
        if (selectedTabForMenu) {
            const tabId = selectedTabForMenu.id;
            const tabName = selectedTabForMenu.name || `시트${selectedTabForMenu.id}`;

            // 먼저 메뉴 닫기
            handleTabMenuClose();

            // 그 다음 편집 모드 활성화 (setTimeout으로 메뉴 닫힌 후 실행)
            setTimeout(() => {
                setEditingTabId(tabId);
                setEditingName(tabName);
            }, 0);
        }
    };

    // 시트 색상 변경 다이얼로그 열기
    const handleOpenColorDialog = () => {
        if (selectedTabForMenu) {
            setTabForColorChange(selectedTabForMenu); // 탭 정보 별도 저장
            setSelectedTabColor(selectedTabForMenu.color || '#ffffff');
            setShowColorDialog(true);
            handleTabMenuClose();
        }
    };

    // 시트 색상 변경 저장
    const handleSaveTabColor = async (color) => {
        if (!tabForColorChange) return;

        const tabId = tabForColorChange.id;

        try {
            // 서버에 시트 색상 업데이트 요청
            const response = await fetch('/api/matrix-board/update-tab-color', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fileId: fileId,
                    tabId: tabId,
                    color: color
                })
            });

            if (!response.ok) {
                throw new Error('시트 색상 업데이트 실패');
            }

            // 로컬 상태 업데이트
            setTabs(prevTabs =>
                prevTabs.map(tab =>
                    tab.id === tabId ? { ...tab, color: color } : tab
                )
            );

            // 웹소켓으로 다른 클라이언트에 전송
            socket.emit('tabs-update', {
                type: 'color',
                fileId: fileId,
                tabId: tabId,
                color: color,
                socketId: socket.id
            });

            setShowColorDialog(false);
            setTabForColorChange(null);
        } catch (error) {
            console.error('시트 색상 변경 오류:', error);
            alert('시트 색상 변경에 실패했습니다.');
        }
    };

    // 선택된 탭 우측에 시트 추가
    const handleAddTabRight = async () => {
        if (!selectedTabForMenu) return;

        try {
            // 서버에서 다음 시트 번호 조회 (삭제된 시트 포함하여 정확한 번호 계산)
            const response = await fetch(`/api/matrix-board/next-sheet-num?fileId=${fileId}`);
            const result = await response.json();

            let newId;
            if (result.success && result.nextSheetNum) {
                newId = result.nextSheetNum;
            } else {
                // API 실패 시 기존 로직 사용 (fallback)
                newId = Math.max(...tabs.map(t => t.id), 0) + 1;
            }

            const currentIndex = tabs.findIndex(t => t.id === selectedTabForMenu.id);
            const newTab = {
                id: newId,
                name: `시트${newId}`
            };

            const newTabs = [...tabs];
            newTabs.splice(currentIndex + 1, 0, newTab);

            console.log('➕ 시트 추가 (우측) 및 선택:', {
                newTabId: newId,
                newTabName: newTab.name,
                insertAfterTabId: selectedTabForMenu.id,
                insertAfterTabName: selectedTabForMenu.name,
                previousActiveTabId: activeTabIdRef.current,
                totalTabs: newTabs.length,
                fileId: fileId,
                fromServer: result.success
            });

            // 새 시트를 활성화 (ref도 즉시 업데이트하여 서버 응답 시 덮어쓰지 않도록)
            activeTabIdRef.current = newId;
            setActiveTabId(newId);

            // 시트 추가 중임을 표시 (서버 응답 시 activeTabId 보호용)
            pendingTabAddRef.current = newId;

            // 히스토리에 저장 (새 시트 ID를 activeTabId로 저장)
            saveTabsToHistory(newTabs, 'add', newId);

            setTabs(newTabs);

            // 서버에 탭 추가 알림
            socket.emit("tabs-update", {
                type: 'add',
                tab: newTab,
                insertAfter: selectedTabForMenu.id,
                fileId: fileId || 'default',
                socketId: socket.id,
                createdBy: user?.name || null
            });
            console.log('탭 추가 (우측) 전송:', newTab);
            handleTabMenuClose();
        } catch (error) {
            console.error('시트 추가 오류:', error);
            alert('시트 추가 중 오류가 발생했습니다.');
        }
    };

    // 시트 복제 (이름에 "(2)" 추가)
    const handleDuplicateTab = async () => {
        if (!selectedTabForMenu) return;

        try {
            // 서버에서 다음 시트 번호 조회 (삭제된 시트 포함하여 정확한 번호 계산)
            const response = await fetch(`/api/matrix-board/next-sheet-num?fileId=${fileId}`);
            const result = await response.json();

            let newId;
            if (result.success && result.nextSheetNum) {
                newId = result.nextSheetNum;
            } else {
                // API 실패 시 기존 로직 사용 (fallback)
                newId = Math.max(...tabs.map(t => t.id), 0) + 1;
            }

            const currentIndex = tabs.findIndex(t => t.id === selectedTabForMenu.id);

            // 복제 번호 계산: 같은 시트명 중 가장 큰 값 + 1 (서버 로직과 동일)
            const baseName = selectedTabForMenu.name.replace(/\s*\(\d+\)$/, '');
            let maxNum = 0;
            tabs.forEach(tab => {
                if (tab.name === baseName) {
                    // 기본 이름이 있으면 1로 간주
                    maxNum = Math.max(maxNum, 1);
                } else if (tab.name.startsWith(baseName)) {
                    // "(숫자)" 패턴 추출
                    const match = tab.name.match(/^(.+?)\s*\((\d+)\)$/);
                    if (match && match[1] === baseName) {
                        const num = parseInt(match[2], 10);
                        maxNum = Math.max(maxNum, num);
                    }
                }
            });

            const newTab = {
                id: newId,
                name: `${baseName} (${maxNum + 1})`
            };

            const newTabs = [...tabs];
            newTabs.splice(currentIndex + 1, 0, newTab);

            console.log('➕ 시트 복제 및 선택:', {
                newTabId: newId,
                newTabName: newTab.name,
                sourceTabId: selectedTabForMenu.id,
                sourceTabName: selectedTabForMenu.name,
                previousActiveTabId: activeTabIdRef.current,
                totalTabs: newTabs.length,
                fileId: fileId,
                fromServer: result.success
            });

            // 새 시트를 활성화 (ref도 즉시 업데이트하여 서버 응답 시 덮어쓰지 않도록)
            activeTabIdRef.current = newId;
            setActiveTabId(newId);

            // 시트 추가 중임을 표시 (서버 응답 시 activeTabId 보호용)
            pendingTabAddRef.current = newId;

            // 히스토리에 저장 (새 시트 ID를 activeTabId로 저장)
            saveTabsToHistory(newTabs, 'duplicate', newId);

            setTabs(newTabs);

            // 서버에 탭 복제 알림 (노드 데이터 복제 포함)
            socket.emit("tabs-update", {
                type: 'duplicate',
                tab: newTab,
                sourceTabId: selectedTabForMenu.id,
                insertAfter: selectedTabForMenu.id,
                fileId: fileId || 'default',
                socketId: socket.id,
                createdBy: user?.name || null
            });
            console.log('탭 복제 전송:', newTab, '원본:', selectedTabForMenu.id);

            // 시트 복제 후 서버에서 노드를 보낼 때까지 약간의 지연 후 명시적으로 노드 요청
            // (서버에서 자동으로 보내지만, 타이밍 문제를 방지하기 위해)
            setTimeout(() => {
                const canvasId = `${fileId}-${newId}-canvas-1`;
                if (socket.connected) {
                    socket.emit("canvas-init", canvasId);
                    console.log('🔄 시트 복제 후 노드 명시적 요청:', canvasId);
                }
            }, 600); // 서버 지연(100ms) + 여유 시간

            handleTabMenuClose();
        } catch (error) {
            console.error('시트 복제 오류:', error);
            alert('시트 복제 중 오류가 발생했습니다.');
        }
    };

    // 메뉴에서 삭제
    const handleDeleteTabFromMenu = () => {
        if (!selectedTabForMenu) return;

        if (tabs.length === 1) {
            alert("최소 하나의 시트는 필요합니다.");
            handleTabMenuClose();
            return;
        }

        const tabId = selectedTabForMenu.id;
        const tabName = selectedTabForMenu.name || `시트${tabId}`;

        // 확인 알림창
        if (!window.confirm(`${tabName}를 영구적으로 삭제하시겠습니까?`)) {
            handleTabMenuClose();
            return; // 취소 시 삭제하지 않음
        }

        const newTabs = tabs.filter(tab => tab.id !== tabId);
        const newActiveTabId = activeTabId === tabId ? newTabs[0].id : activeTabId;

        // 히스토리에 저장
        saveTabsToHistory(newTabs, 'delete');

        setTabs(newTabs);
        if (activeTabId === tabId) {
            setActiveTabId(newActiveTabId);
        }

        // 서버에 탭 삭제 알림
        socket.emit("tabs-update", {
            type: 'delete',
            tabId: tabId,
            fileId: fileId || 'default',
            socketId: socket.id
        });
        console.log('탭 삭제 전송 (메뉴):', tabId);
        handleTabMenuClose();
    };

    // 드래그 시작
    const handleDragStart = (e, tabId) => {
        setDraggedTabId(tabId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', tabId.toString());
    };

    // 드래그 중 (다른 탭 위로)
    const handleDragOver = (e, tabId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedTabId !== tabId) {
            setDragOverTabId(tabId);
        }
    };

    // 드래그가 탭 영역을 벗어남
    const handleDragLeave = (e) => {
        setDragOverTabId(null);
    };

    // 드롭
    const handleDrop = (e, targetTabId) => {
        e.preventDefault();

        if (!draggedTabId || draggedTabId === targetTabId) {
            setDraggedTabId(null);
            setDragOverTabId(null);
            return;
        }

        const draggedIndex = tabs.findIndex(t => t.id === draggedTabId);
        const targetIndex = tabs.findIndex(t => t.id === targetTabId);

        if (draggedIndex === -1 || targetIndex === -1) {
            setDraggedTabId(null);
            setDragOverTabId(null);
            return;
        }

        // 탭 순서 변경
        const newTabs = [...tabs];
        const [draggedTab] = newTabs.splice(draggedIndex, 1);
        newTabs.splice(targetIndex, 0, draggedTab);

        setTabs(newTabs);
        setDraggedTabId(null);
        setDragOverTabId(null);

        // 서버에 순서 변경 알림
        socket.emit("tabs-update", {
            type: 'reorder',
            tabs: newTabs,
            fileId: fileId,
            socketId: socket.id
        });
        console.log('탭 순서 변경:', newTabs.map(t => t.name).join(' → '));
    };

    // 드래그 종료
    const handleDragEnd = () => {
        setDraggedTabId(null);
        setDragOverTabId(null);
    };

    const handleTabClick = (tabId) => {
        const selectedTab = tabs.find(t => t.id === tabId);
        console.log('📑 시트 선택:', {
            tabId: tabId,
            tabName: selectedTab?.name || '알 수 없음',
            fileId: fileId,
            totalTabs: tabs.length
        });
        setActiveTabId(tabId);
        setEditingTabId(null);
    };

    const handleTabDoubleClick = (e, tabId, currentName) => {
        e.stopPropagation();
        // 권한이 없으면 수정 불가
        if (userLoading || !hasPermission('matrixBoard-teamProject-sheet', 'edit')) {
            return;
        }
        setEditingTabId(tabId);
        setEditingName(currentName);
    };

    const handleNameChange = (e) => {
        setEditingName(e.target.value);
    };

    const handleNameBlur = () => {
        if (editingTabId && editingName.trim()) {
            const newName = editingName.trim();

            // 클라이언트에서 완전히 중복된 이름 체크 (자기 자신 제외)
            const isDuplicate = tabs.some(tab =>
                tab.id !== editingTabId && tab.name === newName
            );

            if (isDuplicate) {
                alert('동일한 이름의 시트가 이미 존재합니다.');
                setEditingTabId(null);
                setEditingName("");
                return;
            }

            setTabs(tabs.map(tab =>
                tab.id === editingTabId
                    ? { ...tab, name: newName }
                    : tab
            ));

            // 서버에 탭 이름 변경 알림 (소켓 ID 포함)
            socket.emit("tabs-update", {
                type: 'rename',
                tabId: editingTabId,
                name: newName,
                fileId: fileId || 'default',
                socketId: socket.id
            });
            console.log('탭 이름 변경 전송:', editingTabId, newName);
        }
        setEditingTabId(null);
        setEditingName("");
    };

    const handleNameKeyPress = (e) => {
        if (e.key === "Enter") {
            handleNameBlur();
        } else if (e.key === "Escape") {
            setEditingTabId(null);
            setEditingName("");
        }
    };

    // 페이지 시작 시 데이터 불러오기 (user 로드 완료 후)
    useEffect(() => {
        if (userLoading || !boardBrdCd) return;
        fetch('/api/cad-data', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ brd_cd: boardBrdCd })
        })
            .then(response => response.json())
            .then(data => {
                setCadData(data);
            })
            .catch(error => {
                console.error('CAD 데이터 불러오기 오류:', error);
            });
    }, [userLoading, boardBrdCd]);

    return (
        <>
            <SheetTabsContainer>
                <FixedSection>
                    <button
                        onClick={() => {
                            // location.state에서 이전 탭 정보를 읽어서 해당 탭으로 이동
                            const fromTab = location.state?.fromTab;
                            navigate('/interstellar/matrix-board', {
                                state: { activeTab: fromTab || '공유 프로젝트' }
                            });
                        }}
                        style={{
                            pointerEvents: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textDecoration: 'none',
                            color: '#ccc5b9',
                            height: '40px',
                            width: '40px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <BsList size={32} />
                    </button>
                    <h1
                        style={{
                            pointerEvents: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 'content-fit',
                            textDecoration: 'none',
                            fontSize: '20px',
                            margin: '5px 20px',
                            whiteSpace: 'nowrap'
                        }}>
                        {pageTitle}
                        {boardMapType === 'S' && (
                            <span style={{ fontSize: '12px', fontWeight: 400, color: '#888', marginLeft: '10px', whiteSpace: 'nowrap' }}>
                                기준일자 : {boardEndDt || ''}
                            </span>
                        )}
                    </h1>
                </FixedSection>
                <ScrollableTabsSection>
                    {tabs.map(tab => (
                        <SheetTab
                            key={tab.id}
                            $active={tab.id === activeTabId}
                            $isDragging={draggedTabId === tab.id}
                            $isDragOver={dragOverTabId === tab.id && draggedTabId !== tab.id}
                            $color={tab.color}
                            onClick={() => handleTabClick(tab.id)}
                            onDoubleClick={(e) => handleTabDoubleClick(e, tab.id, tab.name)}
                            draggable={!userLoading && hasPermission('matrixBoard-teamProject-sheet', 'edit') && editingTabId !== tab.id}
                            onDragStart={(e) => handleDragStart(e, tab.id)}
                            onDragOver={(e) => handleDragOver(e, tab.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, tab.id)}
                            onDragEnd={handleDragEnd}
                            title={tab.name}
                        >
                            {editingTabId === tab.id ? (
                                <TabInput
                                    value={editingName}
                                    onChange={handleNameChange}
                                    onBlur={handleNameBlur}
                                    onKeyDown={handleNameKeyPress}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            ) : (
                                <>
                                    <TabLabel $active={tab.id === activeTabId} $dark={isDarkColor(tab.color)}>
                                        {tab.id === activeTabId && '▼ '}
                                        {updatingTabNamesRef.current.has(tab.id) ? '' : tab.name}
                                    </TabLabel>
                                    {!userLoading && hasPermission('matrixBoard-teamProject-sheet', 'edit') && (
                                        <TabMenuButton
                                            className="tab-menu-button"
                                            onClick={(e) => handleTabMenuOpen(e, tab)}
                                            title="시트 메뉴"
                                            $dark={isDarkColor(tab.color)}
                                        >
                                            <MoreVertIcon />
                                        </TabMenuButton>
                                    )}
                                </>
                            )}
                        </SheetTab>
                    ))}
                    {!userLoading && hasPermission('matrixBoard-teamProject-sheet', 'edit') && (
                        <AddTabButton onClick={handleAddTab} title="새 시트 추가">
                            <AddIcon />
                        </AddTabButton>
                    )}
                </ScrollableTabsSection>
            </SheetTabsContainer>

            {/* 탭 메뉴 */}
            <Menu
                anchorEl={tabMenuAnchor}
                open={Boolean(tabMenuAnchor)}
                onClose={handleTabMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '160px',
                        mt: 1.5,
                        ml: 1.5,
                    }
                }}
            >
                <MenuItem
                    onClick={handleOpenRenameTab}
                    sx={{ fontSize: '14px', py: 1 }}
                >
                    시트명 수정
                </MenuItem>
                <MenuItem
                    onClick={handleOpenColorDialog}
                    sx={{ fontSize: '14px', py: 1 }}
                >
                    시트(탭) 색상 변경
                </MenuItem>
                <div style={{ borderTop: '1px solid #e0e0e0', margin: '4px 0' }} />
                <MenuItem
                    onClick={handleAddTabRight}
                    sx={{ fontSize: '14px', py: 1 }}
                >
                    우측에 시트추가
                </MenuItem>
                <MenuItem
                    onClick={handleDuplicateTab}
                    sx={{ fontSize: '14px', py: 1 }}
                >
                    시트 복제
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteTabFromMenu}
                    sx={{ fontSize: '14px', py: 1, color: '#dc3545' }}
                >
                    시트 삭제
                </MenuItem>
            </Menu>

            {/* 시트 색상 변경 다이얼로그 */}
            <Dialog
                open={showColorDialog}
                onClose={() => setShowColorDialog(false)}
                PaperProps={{
                    sx: {
                        width: '240px',
                        maxWidth: '240px',
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '16px', py: 1.5 }}>시트 색상 변경</DialogTitle>
                <DialogContent sx={{ p: 1 }}>
                    <SketchPicker
                        color={selectedTabColor}
                        onChange={(color) => {
                            const hexColor = color.hex;
                            setSelectedTabColor(hexColor);
                        }}
                        disableAlpha={true}
                        width="220px"
                        styles={{
                            default: {
                                picker: {
                                    boxShadow: 'none',
                                    border: 'none',
                                    padding: 0,
                                    width: '220px',
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ py: 1 }}>
                    <Button
                        onClick={() => setShowColorDialog(false)}
                        size="small"
                        sx={{
                            color: '#3e3832',
                            backgroundColor: '#f5f5f4',
                            '&:hover': {
                                backgroundColor: '#e7e5e4'
                            }
                        }}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={() => handleSaveTabColor(selectedTabColor)}
                        size="small"
                        sx={{
                            color: 'white',
                            backgroundColor: '#ccc5b9',
                            '&:hover': {
                                backgroundColor: '#b8b0a5'
                            }
                        }}
                    >
                        저장
                    </Button>
                </DialogActions>
            </Dialog>

            <SheetContainer>

                <SheetContent>
                    {tabs.map(tab => (
                        tab.id === activeTabId && (
                            <div
                                key={tab.id}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: '#fafafa'
                                }}
                            >
                                <ReactFlow
                                    tabId={tab.id}
                                    cadData={cadData}
                                    fileId={fileId}
                                    onTabsRestore={restoreTabsFromHistory}
                                    isPersonal={isPersonal}
                                    isPersonalLoaded={isPersonalLoaded}
                                    boardAutoUpdate={boardAutoUpdate}
                                    boardMapType={boardMapType}
                                    boardBrdCd={boardBrdCd}
                                    boardSesn={boardSesn}
                                />
                            </div>
                        )
                    ))}
                </SheetContent>
            </SheetContainer>

            {/* 세션 만료 모달 */}
            <Dialog
                open={showSessionExpiredModal}
                onClose={handleCloseSessionModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    paddingBottom: '8px'
                }}>
                    세션이 만료되었습니다
                </DialogTitle>
                <DialogContent>
                    <p style={{
                        margin: 0,
                        fontSize: '14px',
                        color: '#666',
                        lineHeight: '1.6'
                    }}>
                        서버와의 연결이 끊어졌습니다. 페이지를 새로고침하여 다시 연결해주세요.
                    </p>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: '8px' }}>
                    <Button
                        onClick={handleCloseSessionModal}
                        variant="outlined"
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            padding: '8px 16px'
                        }}
                    >
                        닫기
                    </Button>
                    <Button
                        onClick={handleRefresh}
                        variant="contained"
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            padding: '8px 16px',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#1565c0'
                            }
                        }}
                    >
                        새로고침
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 연결 중 토스트 */}
            <Snackbar
                open={showReconnectingToast}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={null}
                onClose={() => setShowReconnectingToast(false)}
            >
                <Alert
                    severity="info"
                    icon={false}
                    sx={{
                        width: '100%',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        '& .MuiAlert-message': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: 500
                        }
                    }}
                >
                    <span style={{ marginRight: '8px' }}>🔄</span>
                    연결 중... 잠시만 기다려주세요.
                </Alert>
            </Snackbar>
        </>
    );
}

export default MatrixBoard;